import React, { useEffect, useState, useRef } from 'react'

import { cn } from '../../../global/utils/cn.js'
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionContent,
} from '../../../global/components/ui/accordion.jsx'
import { Checkbox } from '../../../global/components/ui/checkbox.jsx'
import RequestTaskFileRow from './RequestTaskFileRow.jsx'
import { formatDate } from '../utils/documentUtils.js'

const DocumentAccordion = ({
  doc,
  dragOverDocId,
  setDragOverDocId,
  onDragFiles,
  onDeleteFile,
}) => {
  const isOtherAccordion = doc.id === 'unmatched' || doc.isOther
  const [expanded, setExpanded] = useState(false)
  const isFirstRender = useRef(true)

  const itemValue = String(doc.id)
  const docName = doc.name || doc.custom_name
  const fileCount = doc.files?.length || 0
  const requiredCount = doc.settings?.number_of_files
  const isFulfilled =
    requiredCount != null ? fileCount === requiredCount : fileCount > 0
  const isDragOver = dragOverDocId === doc.id

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (doc.files?.length > 0) {
      setExpanded(true)
    }
  }, [doc.files?.length])

  const handleDragOver = e => {
    e.preventDefault()
    setDragOverDocId(doc.id)
  }

  const handleDragLeave = e => {
    // Only clear if we're leaving the accordion itself, not a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDocId(null)
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    setDragOverDocId(null)
    const fileId = e.dataTransfer.getData('fileId')
    const fromDocId = e.dataTransfer.getData('fromDocId')
    const toDocId = doc.id
    onDragFiles(fileId, fromDocId, toDocId)
  }

  const handleDragStart = (e, file) => {
    e.dataTransfer.setData('fileId', file._id)
    e.dataTransfer.setData('fromDocId', doc.id)
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={expanded ? itemValue : ''}
      onValueChange={value => setExpanded(value === itemValue)}
    >
      <AccordionItem
        value={itemValue}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'tw-rounded-control tw-border tw-px-3 tw-transition-colors',
          isDragOver && 'tw-border-dashed tw-border-primary tw-bg-accent',
        )}
      >
        <AccordionHeader className="tw-gap-2">
          {!isOtherAccordion && (
            <Checkbox
              checked={isFulfilled}
              aria-readonly="true"
              aria-label={`${docName} fulfilled`}
              tabIndex={-1}
              onClick={e => e.preventDefault()}
            />
          )}
          <AccordionTrigger className="tw-py-3">
            <span className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2">
              <span>{docName}</span>
              {isOtherAccordion ? (
                <span className="u-muted tw-font-normal">
                  ({fileCount} file{fileCount !== 1 ? 's' : ''})
                </span>
              ) : requiredCount ? (
                <span className="u-muted tw-font-normal">
                  ({fileCount}/{requiredCount})
                </span>
              ) : null}
            </span>
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>
          {!isOtherAccordion && (doc.range_from_date || doc.range_to_date) && (
            <p className="tw-mb-4 tw-text-sm">
              <strong>From:</strong> {formatDate(doc.range_from_date)} |{' '}
              <strong>To:</strong> {formatDate(doc.range_to_date)}
            </p>
          )}

          {fileCount > 0 ? (
            doc.files.map(file => (
              <RequestTaskFileRow
                key={file._id}
                file={file}
                draggable
                onDragStart={e => handleDragStart(e, file)}
                onDelete={() => onDeleteFile(file._id, doc.id)}
              />
            ))
          ) : (
            <p className="u-muted tw-mb-0 tw-text-sm">No files uploaded yet.</p>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default DocumentAccordion
